import React from "react";
import { Group, Button } from "@mantine/core";
import dayjs from "dayjs";

export interface DateRangeQuickPillsProps {
  /**
   * Currently active date range [startDate, endDate]
   */
  value?: [Date | string | null, Date | string | null] | null;
  /**
   * Callback fired when a quick preset pill is clicked
   */
  onChange: (range: [Date, Date]) => void;
  /**
   * Optional disabled flag (e.g. when document number search is active)
   */
  disabled?: boolean;
}

export function DateRangeQuickPills({
  value,
  onChange,
  disabled = false,
}: DateRangeQuickPillsProps) {
  const today = dayjs();

  const presets: { label: string; getRange: () => [Date, Date] }[] = [
    {
      label: "Today",
      getRange: () => [
        today.startOf("day").toDate(),
        today.endOf("day").toDate(),
      ],
    },
    {
      label: "Yesterday",
      getRange: () => [
        today.subtract(1, "day").startOf("day").toDate(),
        today.subtract(1, "day").endOf("day").toDate(),
      ],
    },
    {
      label: "Last 7 Days",
      getRange: () => [
        today.subtract(6, "day").startOf("day").toDate(),
        today.endOf("day").toDate(),
      ],
    },
    {
      label: "This Month",
      getRange: () => [
        today.startOf("month").toDate(),
        today.endOf("month").toDate(),
      ],
    },
  ];

  const isPresetActive = (presetRange: [Date, Date]) => {
    if (!value || !value[0] || !value[1]) return false;
    const dStart = dayjs(value[0]);
    const dEnd = dayjs(value[1]);
    if (!dStart.isValid() || !dEnd.isValid()) return false;
    const [pStart, pEnd] = presetRange;
    return dStart.isSame(pStart, "day") && dEnd.isSame(pEnd, "day");
  };

  return (
    <Group
      spacing={6}
      mt={6}
      style={{
        overflowX: "auto",
        flexWrap: "nowrap",
        WebkitOverflowScrolling: "touch",
        paddingBottom: 4,
        width: "100%",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        opacity: disabled ? 0.5 : 1,
      }}
      sx={{
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {presets.map((preset) => {
        const range = preset.getRange();
        const active = isPresetActive(range);

        return (
          <Button
            key={preset.label}
            size="xs"
            radius="xl"
            variant={active ? "filled" : "outline"}
            color="cap-blue"
            disabled={disabled}
            aria-pressed={active}
            aria-label={`Filter by ${preset.label}`}
            onClick={() => {
              if (!disabled) {
                onChange(range);
              }
            }}
            styles={{
              root: {
                minHeight: 34,
                padding: "0 12px",
                whiteSpace: "nowrap",
                flexShrink: 0,
                fontWeight: 500,
              },
            }}
          >
            {preset.label}
          </Button>
        );
      })}
    </Group>
  );
}

export default DateRangeQuickPills;
